import React, { useRef, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { patch, post } from './httpClient';
import { toast } from 'react-toastify';
import LayoutStructureEditor, { normalize, syncMobileProps } from './LayoutStructureEditor';

function LayoutForm({ websiteId, initial, onSaved, onCancel }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name ?? '');
  const [schemaType, setSchemaType] = useState(initial?.schema_type ?? 'article');
  const [structure, setStructure] = useState(initial ? { rows: initial.rows ?? [], mobile: initial.mobile_template ?? { components: [] } } : { rows: [] });
  const [saving, setSaving] = useState(false);

  // Snapshot of the form's starting point, so Cancel can tell whether there's anything to lose.
  // A layout can take a while to build (rows, columns, per-property settings) — losing that to
  // a stray click deserves a confirmation, the same way the browser warns before a tab close.
  const initialSnapshotRef = useRef(JSON.stringify({ name: initial?.name ?? '', schemaType: initial?.schema_type ?? 'article', structure }));
  const isDirty = () => JSON.stringify({ name, schemaType, structure }) !== initialSnapshotRef.current;

  const handleCancel = () => {
    if (isDirty() && !window.confirm('Discard unsaved changes to this layout?')) return;
    onCancel();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required.');
      return;
    }
    const normalized = normalize(structure);
    const overWidthRow = normalized.rows.findIndex(
      (row) => (row.columns ?? []).reduce((s, c) => s + (Number(c.width) || 0), 0) > 100.0001
    );
    if (overWidthRow !== -1) {
      toast.error(`Row ${overWidthRow + 1}: column widths exceed 100%.`);
      return;
    }
    const hasUnnamedProperty = normalized.rows.some(
      (row) => (row.columns ?? []).some((c) => (c.components ?? []).some((p) => !String(p.name ?? '').trim()))
    );
    if (hasUnnamedProperty) {
      toast.error('Every property needs a name — a page using this layout won\'t be able to show or edit it otherwise.');
      return;
    }
    // Property values are stored as a flat {name: value} map across the whole template, so two
    // properties sharing a name — even in different rows/columns — silently overwrite each
    // other's value instead of being independently editable.
    const allNames = normalized.rows.flatMap((row) => (row.columns ?? []).flatMap((c) => (c.components ?? []).map((p) => String(p.name ?? '').trim())));
    const seen = new Set();
    const duplicateName = allNames.find((n) => {
      if (!n) return false;
      if (seen.has(n)) return true;
      seen.add(n);
      return false;
    });
    if (duplicateName) {
      toast.error(`Property name "${duplicateName}" is used more than once — each property needs a unique name.`);
      return;
    }
    try {
      setSaving(true);
      const syncedMobileProps = syncMobileProps(normalized.rows, normalized.mobile.components);
      const payload = { name: name.trim(), schema_type: schemaType, rows: normalized.rows, mobile: { components: syncedMobileProps } };
      let result;
      if (isEdit) {
        result = await patch(`websites/${websiteId}/layouts/${initial.id}`, payload);
      } else {
        result = await post(`websites/${websiteId}/layouts`, payload);
      }
      onSaved(result.layout);
    } catch {
      /* httpClient shows toast */
    } finally {
      setSaving(false);
    }
  };

  return (
    // Rendered as a plain div (not a <form>) because this component is also embedded inside
    // the page editor's own <form> — nested <form> elements are invalid HTML and let a submit
    // here bubble up and trigger the outer form's submit handler too.
    <Form as="div" className="layout-form mb-3">
      <Form.Group className="mb-3 seo-field-full">
        <Form.Label>Name</Form.Label>
        <Form.Control
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Layout name"
        />
      </Form.Group>

      <Form.Group className="mb-3 seo-field-full">
        <Form.Label>Structured data type</Form.Label>
        <Form.Select value={schemaType} onChange={(e) => setSchemaType(e.target.value)}>
          <option value="article">Article — blog posts, generic pages (default)</option>
          <option value="visual_artwork">Visual artwork — individual pieces, for richer search results</option>
        </Form.Select>
        <Form.Text muted>
          Controls the schema.org type search engines see for pages built with this layout.
        </Form.Text>
      </Form.Group>

      <LayoutStructureEditor value={structure} onChange={setStructure} websiteId={websiteId} />

      <div className="form-actions form-actions--sticky mt-3">
        <Button type="button" className="primary-button" onClick={handleSubmit} disabled={saving}>
          {saving ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : 'Save'}
        </Button>
        <Button type="button" className="primary-button" onClick={handleCancel} disabled={saving}>Cancel</Button>
      </div>
    </Form>
  );
}

export default LayoutForm;
